import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DatePicker } from "../components/ui/date-picker";
import { Handshake, Receipt, Package, Plus, Pencil, Trash2, ArrowDownToLine, CheckCircle2, Globe, Mail, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import UpgradeBanner from "../components/billing/UpgradeBanner";
import { usePlan } from "../lib/usePlan";

const EXPENSE_CATEGORIES = {
  trasferta: "Trasferta", materiale: "Materiale", rinfresco: "Rinfresco",
  rimborso_arbitro: "Arbitro", manutenzione: "Manutenzione", altro: "Altro",
};
const EXPENSE_STATUS = {
  da_approvare: { label: "Da approvare", cls: "bg-amber-100 text-amber-700" },
  approvata: { label: "Approvata", cls: "bg-sky-100 text-sky-700" },
  rimborsata: { label: "Rimborsata", cls: "bg-emerald-100 text-emerald-700" },
  rifiutata: { label: "Rifiutata", cls: "bg-red-100 text-red-700" },
};
const INV_CATEGORIES = {
  divisa: "Divisa", pallone: "Pallone", materiale_tecnico: "Materiale tecnico", kit_gioco: "Kit gioco", altro: "Altro",
};
const SPONSOR_VISIBILITY = {
  main: { label: "Main", cls: "bg-amber-100 text-amber-700" },
  secondary: { label: "Secondary", cls: "bg-slate-100 text-slate-700" },
  technical: { label: "Tecnico", cls: "bg-sky-100 text-sky-700" },
};

export default function ClubManagementPage() {
  const { activeTeam, canEdit } = useTeams();
  const plan = usePlan();
  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;
  if (!canEdit) return <p className="text-slate-500">Sezione riservata a Mister e Dirigenti.</p>;

  const sponsorsUnlocked = plan?.has?.("sponsors");
  const siteUnlocked = plan?.has?.("public_site");

  return (
    <div className="space-y-6" data-testid="club-management-page">
      <div>
        <div className="label-eyebrow">Gestione Società</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Sponsor, Spese, Magazzino</h1>
        <p className="text-slate-500">Anagrafica sponsor, note spese staff e gestione materiali</p>
      </div>
      <Tabs defaultValue={sponsorsUnlocked ? "sponsors" : "expenses"}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="sponsors" data-testid="tab-sponsors"><Handshake size={14} className="mr-1" /> Sponsor</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses"><Receipt size={14} className="mr-1" /> Note spese</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory"><Package size={14} className="mr-1" /> Magazzino</TabsTrigger>
          <TabsTrigger value="website" data-testid="tab-website"><Globe size={14} className="mr-1" /> Sito pubblico</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter"><Mail size={14} className="mr-1" /> Newsletter</TabsTrigger>
        </TabsList>
        <TabsContent value="sponsors" className="mt-4">
          {sponsorsUnlocked ? <SponsorTab team={activeTeam} /> : <UpgradeBanner feature="Gestione Sponsor" requiredPlan="club" />}
        </TabsContent>
        <TabsContent value="expenses" className="mt-4"><ExpensesTab team={activeTeam} /></TabsContent>
        <TabsContent value="inventory" className="mt-4"><InventoryTab team={activeTeam} /></TabsContent>
        <TabsContent value="website" className="mt-4">
          {siteUnlocked ? <WebsiteTab team={activeTeam} /> : <UpgradeBanner feature="Sito pubblico società" requiredPlan="club" />}
        </TabsContent>
        <TabsContent value="newsletter" className="mt-4"><NewsletterTab team={activeTeam} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ SPONSORS ============
function SponsorTab({ team }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySponsor(team));
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/teams/${team.id}/sponsors`).catch(() => ({ data: [] }));
    setItems(r.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const totalValue = items.reduce((s, x) => s + (Number(x.value_eur) || 0), 0);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome richiesto");
    const payload = {
      ...form, team_id: team.id, season: team.season || null,
      value_eur: form.value_eur === "" ? null : Number(form.value_eur),
    };
    if (editingId) await api.put(`/sponsors/${editingId}`, payload);
    else await api.post(`/sponsors`, payload);
    toast.success("Sponsor salvato");
    setOpen(false); setEditingId(null); setForm(emptySponsor(team));
    load();
  };

  const handleNew = () => { setForm(emptySponsor(team)); setEditingId(null); setOpen(true); };
  const handleEdit = (s) => {
    setForm({ ...s, value_eur: s.value_eur ?? "" });
    setEditingId(s.id); setOpen(true);
  };
  const remove = async (s) => {
    if (!window.confirm(`Eliminare lo sponsor "${s.name}"?`)) return;
    await api.delete(`/sponsors/${s.id}`); load();
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo caricato");
    } catch (e) { toast.error("Errore upload"); }
    setUploading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-slate-500">{items.length} sponsor · Valore totale stagione <strong className="text-slate-800">€ {totalValue.toFixed(2)}</strong></p>
        </div>
        <Button onClick={handleNew} size="sm" className="bg-sky-500 hover:bg-sky-600" data-testid="new-sponsor-btn">
          <Plus size={14} className="mr-1" /> Nuovo sponsor
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((s) => {
          const v = SPONSOR_VISIBILITY[s.visibility] || SPONSOR_VISIBILITY.secondary;
          return (
            <Card key={s.id} className="p-4 border-slate-200 group" data-testid={`sponsor-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0 border">
                  {s.logo_url ? <img src={s.logo_url} alt={s.name} className="w-full h-full object-contain" /> : <Handshake size={24} className="text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{s.name}</div>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${v.cls}`}>{v.label}</span>
                  {s.value_eur != null && <div className="text-xs text-slate-500 mt-1">€ {Number(s.value_eur).toFixed(2)}</div>}
                </div>
                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(s)}><Pencil size={13} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s)}><Trash2 size={13} className="text-red-500" /></Button>
                </div>
              </div>
              {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline mt-2 block truncate">{s.website}</a>}
              {s.contact_email && <p className="text-xs text-slate-400 mt-1 truncate">{s.contact_email}</p>}
            </Card>
          );
        })}
        {items.length === 0 && <Card className="p-10 col-span-full text-center text-slate-400 border-dashed"><Handshake size={28} className="mx-auto mb-2 text-slate-300" />Nessuno sponsor.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica sponsor" : "Nuovo sponsor"}</DialogTitle>
            <DialogDescription className="sr-only">Dati anagrafici sponsor</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Visibilità</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SPONSOR_VISIBILITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valore (€)</Label>
                <Input type="number" step="0.01" min="0" value={form.value_eur} onChange={(e) => setForm({ ...form, value_eur: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Logo</Label>
              <Input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} disabled={uploading} />
              {form.logo_url && <img src={form.logo_url} alt="logo" className="mt-2 h-16 object-contain" />}
            </div>
            <div>
              <Label>Sito web</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contatto</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contratto da</Label>
                <DatePicker value={form.contract_start} onChange={(v) => setForm({ ...form, contract_start: v })} />
              </div>
              <div>
                <Label>Contratto a</Label>
                <DatePicker value={form.contract_end} onChange={(v) => setForm({ ...form, contract_end: v })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Attivo (mostrato sul sito pubblico)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-sponsor-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const emptySponsor = (team) => ({
  name: "", logo_url: "", website: "", contact_name: "", contact_email: "", contact_phone: "",
  value_eur: "", contract_start: "", contract_end: "", visibility: "secondary", is_active: true, season: team?.season || "",
});

// ============ EXPENSES ============
function ExpensesTab({ team }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyExpense(team));
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/teams/${team.id}/expenses`).catch(() => ({ data: [] }));
    setItems(r.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const sum = (st) => items.filter((x) => x.status === st).reduce((s, x) => s + Number(x.amount || 0), 0);
    return { pending: sum("da_approvare") + sum("approvata"), reimbursed: sum("rimborsata") };
  }, [items]);

  const submit = async () => {
    if (!form.submitter_name.trim() || !form.description.trim() || !form.expense_date) {
      return toast.error("Compila Nome, Descrizione e Data");
    }
    const payload = { ...form, team_id: team.id, season: team.season || null, amount: Number(form.amount || 0) };
    if (editingId) await api.put(`/expenses/${editingId}`, payload);
    else await api.post(`/expenses`, payload);
    toast.success("Spesa salvata"); setOpen(false); setEditingId(null); setForm(emptyExpense(team));
    load();
  };
  const uploadReceipt = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_url: data.url }));
      toast.success("Scontrino caricato");
    } catch (e) { toast.error("Errore upload"); }
    setUploading(false);
  };
  const handleNew = () => { setForm(emptyExpense(team)); setEditingId(null); setOpen(true); };
  const handleEdit = (e) => { setForm({ ...e, amount: String(e.amount || "") }); setEditingId(e.id); setOpen(true); };
  const setStatus = async (e, status) => {
    await api.put(`/expenses/${e.id}`, { ...e, status });
    load();
  };
  const remove = async (e) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/expenses/${e.id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex gap-4 text-sm">
          <div><span className="text-slate-500">Da rimborsare:</span> <strong className="text-amber-700">€ {totals.pending.toFixed(2)}</strong></div>
          <div><span className="text-emerald-600">Rimborsate:</span> <strong className="text-emerald-700">€ {totals.reimbursed.toFixed(2)}</strong></div>
        </div>
        <Button onClick={handleNew} size="sm" className="bg-sky-500 hover:bg-sky-600" data-testid="new-expense-btn">
          <Plus size={14} className="mr-1" /> Nuova spesa
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Data</th>
              <th className="text-left px-4 py-2">Chi</th>
              <th className="text-left px-4 py-2">Descrizione</th>
              <th className="text-right px-4 py-2">€</th>
              <th className="text-center px-4 py-2">Stato</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => {
              const st = EXPENSE_STATUS[e.status] || EXPENSE_STATUS.da_approvare;
              return (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`expense-${e.id}`}>
                  <td className="px-4 py-2 text-xs">{e.expense_date}</td>
                  <td className="px-4 py-2 font-medium text-xs">{e.submitter_name}<br /><span className="text-slate-400 text-[10px]">{EXPENSE_CATEGORIES[e.category]}</span></td>
                  <td className="px-4 py-2 text-xs max-w-xs">{e.description}{e.receipt_url && <a href={e.receipt_url} target="_blank" rel="noreferrer" className="ml-1 text-sky-600 text-[10px]">[scontrino]</a>}</td>
                  <td className="px-4 py-2 text-right font-mono">€ {Number(e.amount).toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">
                    <Select value={e.status} onValueChange={(v) => setStatus(e, v)}>
                      <SelectTrigger className={`h-7 text-xs ${st.cls} border-0`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(EXPENSE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(e)}><Pencil size={13} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(e)}><Trash2 size={13} className="text-red-500" /></Button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={6}>Nessuna spesa registrata.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica spesa" : "Nuova nota spese"}</DialogTitle>
            <DialogDescription className="sr-only">Registra una nota spese</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input value={form.submitter_name} onChange={(ev) => setForm({ ...form, submitter_name: ev.target.value })} data-testid="expense-name" />
              </div>
              <div>
                <Label>Ruolo</Label>
                <Input value={form.submitter_role} onChange={(ev) => setForm({ ...form, submitter_role: ev.target.value })} placeholder="Mister, Dirigente..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data</Label>
                <DatePicker value={form.expense_date} onChange={(v) => setForm({ ...form, expense_date: v })} />
              </div>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea rows={2} value={form.description} onChange={(ev) => setForm({ ...form, description: ev.target.value })} placeholder="Es. Trasferta Rimini autostrada+pranzo squadra" data-testid="expense-desc" />
            </div>
            <div>
              <Label>Importo (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(ev) => setForm({ ...form, amount: ev.target.value })} data-testid="expense-amount" />
            </div>
            <div>
              <Label>Scontrino / Ricevuta</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={(ev) => uploadReceipt(ev.target.files?.[0])} disabled={uploading} />
              {form.receipt_url && <p className="text-xs text-emerald-600 mt-1">✓ Caricato</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-expense-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const emptyExpense = (team) => ({
  submitter_name: "", submitter_role: "", category: "altro", description: "",
  amount: "", expense_date: new Date().toISOString().slice(0, 10), receipt_url: "", status: "da_approvare", season: team?.season || "",
});

// ============ INVENTORY ============
function InventoryTab({ team }) {
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyItem());
  const [assignFor, setAssignFor] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignForm, setAssignForm] = useState({ player_id: "", quantity: 1, assigned_date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = useCallback(async () => {
    const [i, p] = await Promise.all([
      api.get(`/teams/${team.id}/inventory`).catch(() => ({ data: [] })),
      api.get(`/teams/${team.id}/players`).catch(() => ({ data: [] })),
    ]);
    setItems(i.data); setPlayers(p.data);
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const loadAssignments = useCallback(async (itemId) => {
    const r = await api.get(`/inventory/${itemId}/assignments`).catch(() => ({ data: [] }));
    setAssignments(r.data);
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome richiesto");
    const payload = { ...form, team_id: team.id, quantity_total: Number(form.quantity_total || 0), quantity_available: Number(form.quantity_available || form.quantity_total || 0), unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost) };
    if (editingId) await api.put(`/inventory/${editingId}`, payload);
    else await api.post(`/inventory`, payload);
    toast.success("Articolo salvato"); setOpen(false); setForm(emptyItem()); setEditingId(null);
    load();
  };
  const handleNew = () => { setForm(emptyItem()); setEditingId(null); setOpen(true); };
  const handleEdit = (it) => { setForm({ ...it, quantity_total: String(it.quantity_total), quantity_available: String(it.quantity_available), unit_cost: it.unit_cost ?? "" }); setEditingId(it.id); setOpen(true); };
  const remove = async (it) => { if (!window.confirm(`Eliminare "${it.name}"?`)) return; try { await api.delete(`/inventory/${it.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };

  const openAssign = async (it) => { setAssignFor(it); setAssignForm({ player_id: "", quantity: 1, assigned_date: new Date().toISOString().slice(0, 10), notes: "" }); await loadAssignments(it.id); };
  const submitAssign = async () => {
    if (!assignForm.player_id) return toast.error("Seleziona giocatore");
    try {
      await api.post(`/inventory/${assignFor.id}/assignments`, assignForm);
      toast.success("Consegna registrata");
      await Promise.all([load(), loadAssignments(assignFor.id)]);
      setAssignForm({ player_id: "", quantity: 1, assigned_date: new Date().toISOString().slice(0, 10), notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const returnAssignment = async (a) => {
    await api.post(`/inventory-assignments/${a.id}/return`);
    await Promise.all([load(), loadAssignments(assignFor.id)]);
  };

  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleNew} size="sm" className="bg-sky-500 hover:bg-sky-600" data-testid="new-inventory-btn">
          <Plus size={14} className="mr-1" /> Nuovo articolo
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const consegnati = (it.quantity_total || 0) - (it.quantity_available || 0);
          return (
            <Card key={it.id} className="p-4 border-slate-200" data-testid={`item-${it.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Package size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{it.name}</div>
                  <div className="text-xs text-slate-500">{INV_CATEGORIES[it.category]}{it.size ? ` · Tg ${it.size}` : ""}</div>
                  <div className="text-xs mt-1">
                    <span className="text-emerald-600 font-semibold">{it.quantity_available}</span>
                    <span className="text-slate-400"> / {it.quantity_total} disponibili</span>
                    {consegnati > 0 && <span className="text-slate-500"> · {consegnati} consegnati</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(it)}><Pencil size={13} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={13} className="text-red-500" /></Button>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => openAssign(it)} className="w-full mt-3" data-testid={`assign-${it.id}`}>
                <ArrowDownToLine size={12} className="mr-1" /> Gestisci consegne
              </Button>
            </Card>
          );
        })}
        {items.length === 0 && <Card className="p-10 col-span-full text-center text-slate-400 border-dashed"><Package size={28} className="mx-auto mb-2 text-slate-300" />Nessun articolo in magazzino.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica articolo" : "Nuovo articolo magazzino"}</DialogTitle>
            <DialogDescription className="sr-only">Articolo magazzino</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Maglia Home" data-testid="item-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(INV_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Taglia</Label>
                <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="XS/S/42..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantità totale</Label>
                <Input type="number" min="0" value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: e.target.value })} />
              </div>
              <div>
                <Label>Disponibili</Label>
                <Input type="number" min="0" value={form.quantity_available} onChange={(e) => setForm({ ...form, quantity_available: e.target.value })} placeholder="(default=totale)" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Costo unit. (€)</Label>
                <Input type="number" step="0.01" min="0" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
              </div>
              <div>
                <Label>Ubicazione</Label>
                <Input value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} placeholder="Magazzino A" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-item-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consegne · {assignFor?.name}</DialogTitle>
            <DialogDescription className="sr-only">Gestisci consegne articolo</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Card className="p-3 bg-slate-50/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Giocatore</Label>
                  <Select value={assignForm.player_id} onValueChange={(v) => setAssignForm({ ...assignForm, player_id: v })}>
                    <SelectTrigger data-testid="assign-player"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantità</Label>
                  <Input type="number" min="1" value={assignForm.quantity} onChange={(e) => setAssignForm({ ...assignForm, quantity: Number(e.target.value) })} />
                </div>
              </div>
              <Button onClick={submitAssign} size="sm" className="mt-2 bg-emerald-500 hover:bg-emerald-600" data-testid="save-assign-btn">
                <Plus size={12} className="mr-1" /> Registra consegna
              </Button>
            </Card>

            <div className="space-y-1">
              <h4 className="font-semibold text-sm">Storico</h4>
              {assignments.map((a) => (
                <div key={a.id} className="p-2 rounded-lg border border-slate-200 flex items-center gap-2 text-sm" data-testid={`assignment-${a.id}`}>
                  <span className="flex-1">{playerMap[a.player_id]?.last_name} {playerMap[a.player_id]?.first_name} · q.{a.quantity} · {a.assigned_date}</span>
                  {a.returned_date ? (
                    <span className="text-xs text-emerald-600"><CheckCircle2 size={12} className="inline mr-1" />Reso {a.returned_date}</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => returnAssignment(a)}>Reso</Button>
                  )}
                </div>
              ))}
              {assignments.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Nessuna consegna</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const emptyItem = () => ({ name: "", category: "altro", size: "", quantity_total: "1", quantity_available: "", unit_cost: "", storage_location: "", notes: "" });

// ============ WEBSITE TAB ============
function WebsiteTab({ team }) {
  const [slug, setSlug] = useState(team.public_slug || "");
  const [enabled, setEnabled] = useState(!!team.public_enabled);
  const [description, setDescription] = useState(team.public_description || "");
  const [facebook, setFacebook] = useState(team.public_social_facebook || "");
  const [instagram, setInstagram] = useState(team.public_social_instagram || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: team.name, category: team.category, season: team.season || "", color: team.color || "#0ea5e9",
        logo_url: team.logo_url || null, legal_name: team.legal_name || "", fiscal_code: team.fiscal_code || "",
        vat_number: team.vat_number || "", address: team.address || "", phone: team.phone || "", email: team.email || "",
        figc_affiliation_code: team.figc_affiliation_code || "", president_name: team.president_name || "",
        public_slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") || null,
        public_enabled: enabled, public_description: description,
        public_social_facebook: facebook, public_social_instagram: instagram,
      };
      await api.put(`/teams/${team.id}`, payload);
      toast.success("Sito pubblico aggiornato. Ricarica la pagina per applicare.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const publicUrl = slug ? `${window.location.origin}/club/${slug}` : null;

  return (
    <Card className="p-5 border-slate-200 max-w-2xl">
      <h3 className="font-heading font-semibold text-lg">Sito pubblico della società</h3>
      <p className="text-xs text-slate-500 mb-4">Pagina vetrina con prossime partite, risultati, sponsor e contatti. Visibile a chiunque abbia il link.</p>

      <div className="space-y-3">
        <div>
          <Label>URL pubblico</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">{window.location.origin}/club/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="asd-calcio-rossi" data-testid="public-slug" />
          </div>
          {publicUrl && enabled && (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-600 mt-1 inline-block">
              → Apri {publicUrl}
            </a>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="public-enabled" />
          <span>Pubblica il sito (accessibile pubblicamente)</span>
        </label>
        <div>
          <Label>Descrizione società / mission</Label>
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Es. La nostra società educa allo sport dal 1985..." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Facebook</Label>
            <Input value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="https://facebook.com/..." />
          </div>
          <div>
            <Label>Instagram</Label>
            <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/..." />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="save-public-btn">
          {saving ? "Salvataggio..." : "Salva impostazioni sito"}
        </Button>
      </div>
    </Card>
  );
}


// ============ NEWSLETTER ============
function NewsletterTab({ team }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!team?.id) return;
    setLoading(true);
    api
      .get(`/teams/${team.id}/newsletter/settings`)
      .then(({ data }) => setEnabled(!!data.enabled))
      .catch(() => setEnabled(false))
      .finally(() => setLoading(false));
  }, [team?.id]);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put(`/teams/${team.id}/newsletter/settings`, { enabled: next });
      setEnabled(next);
      toast.success(next ? "Newsletter attivata: invio ogni lunedì ore 08:00" : "Newsletter disattivata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreview(true);
    try {
      const { data } = await api.post(`/teams/${team.id}/newsletter/preview`);
      toast.success(
        `Anteprima inviata alla tua email. ${
          data.stats
            ? `(${data.stats.trainings} allenamenti · ${data.stats.matches} partite)`
            : ""
        }`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio anteprima");
    } finally {
      setPreview(false);
    }
  };

  return (
    <Card className="p-6 max-w-2xl" data-testid="newsletter-settings-card">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
          <Sparkles size={20} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-lg">Newsletter settimanale AI</h3>
          <p className="text-sm text-slate-500 mt-1">
            Ogni lunedì alle 08:00 (Europe/Rome), l&apos;AI legge le metriche
            della squadra della settimana appena conclusa (allenamenti, presenze,
            partite, top valutazioni) e invia via email un riassunto amichevole
            a tutti i genitori registrati.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={loading || saving}
          onClick={() => save(!enabled)}
          className={`relative h-6 w-11 rounded-full transition ${
            enabled ? "bg-emerald-500" : "bg-slate-300"
          } disabled:opacity-50`}
          data-testid="newsletter-toggle"
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
              enabled ? "left-[22px]" : "left-0.5"
            }`}
          />
        </button>
        <div className="flex-1">
          <div className="font-semibold text-sm">
            {loading ? "Caricamento…" : enabled ? "Attiva" : "Non attiva"}
          </div>
          <div className="text-xs text-slate-500">
            {enabled ? "Prossimo invio: lunedì ore 08:00" : "Nessun invio programmato"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={runPreview}
          disabled={preview}
          variant="outline"
          className="gap-2"
          data-testid="newsletter-preview-btn"
        >
          {preview ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Invia anteprima alla mia email
        </Button>
      </div>

      <p className="mt-4 text-xs text-slate-400">
        L&apos;anteprima usa i dati reali della tua squadra ma viene inviata solo
        al tuo indirizzo. Costa 1 chiamata Claude + 1 email Resend.
      </p>
    </Card>
  );
}
